/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {ColDef} from 'ag-grid-community';
import uniqBy from 'lodash.uniqby';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SCIPendingSettings} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {BulkSCIStyleProps} from './BulkSCISettings';

export interface SCIWifiTabProps extends BulkSCIStyleProps {
  wifiNetworks: any;
  classes: {
    [key: string]: string;
  };
  handleNetworkChange: (e: ChangeEvent<HTMLInputElement>) => void;
  noUpdate: boolean;
  onAddWifiNetwork: MouseEventHandler;
  onRemoveWifiNetwork: Function;
  sciPendingSettingData: SCIPendingSettings;
  newWifiNetwork: GenericObject;
}

const textFieldStyleOverridesWifi = () => ({
  root : {
    '&&' : {
      width : '94%'
    }
  },
  inputRoot : {
    '& input' : {
      height      : '35px',
      paddingLeft : '11px'
    }
  }
});

/**
 * Display SCI Device Wifi infomation
 */
export const SCIWifiTab = ({
  wifiNetworks,
  classes,
  handleNetworkChange,
  noUpdate,
  onAddWifiNetwork,
  onRemoveWifiNetwork,
  sciPendingSettingData,
  newWifiNetwork,
  isBulkAction = false
}: SCIWifiTabProps) => {
  const [refreshRequired, setRefreshRequired] = useState(false);
  useEffect(() => {
    setRefreshRequired(true);
    setTimeout(() => {
      setRefreshRequired(false);
    }, 500);
  }, [wifiNetworks]);

  const isDisabled = useCallback((row): boolean => {
    const wifiNetworkPending = sciPendingSettingData?.wifiNetworks?.networks;
    const onlyInArr1 = Array.isArray(wifiNetworkPending) &&
      wifiNetworkPending.filter(
        (pending) => pending.ssid === row.ssid &&
        pending.pass === row.pass);
    return Array.isArray(onlyInArr1) && onlyInArr1.length > 0;
  }, [sciPendingSettingData]);

  const columnComponents = useMemo(() => ({
    deleteCellRenderer : item => (
      <IconButton icon={<SystemDeleteIcon />}
        disabled = {isDisabled(item?.data)}
        style={{paddingTop: '10px'}}
        onClick={function() {
          onRemoveWifiNetwork(item?.data?.ssid);
        }}/>
    )
  }), [wifiNetworks]);

  const wifiNetworksData = wifiNetworks?.wifiNetworks?.networks;
  const uniqueWifiNetworks = uniqBy(wifiNetworksData, 'ssid');

  return (
    <div>
      <div className={isBulkAction ? classes.bulkFlexStyle : classes.flexStyle}>
        <FormItem disableMinWidth customWidth={5}>
          <FormTextField fieldLabel={translate('admin-devices:view.sciSettings.ssid')}
            placeholder={translate('admin-devices:view.sciSettings.enterSSID')}
            name={'ssid'}
            disabled={noUpdate}
            styleOverrides={textFieldStyleOverridesWifi}
            value={newWifiNetwork.ssid}
            onChange={handleNetworkChange}/>
        </FormItem>
        <FormItem disableMinWidth customWidth={5}>
          <FormTextField fieldLabel={translate('admin-devices:view.sciSettings.passphrase')}
            placeholder={translate('admin-devices:view.sciSettings.enterPassphrase')}
            name={'pass'}
            disabled={noUpdate}
            styleOverrides={textFieldStyleOverridesWifi}
            value={newWifiNetwork.pass}
            onChange={handleNetworkChange}/>
        </FormItem>
        <PrimaryButton className={classes.addBtn}
          disabled={noUpdate || !(newWifiNetwork.ssid && newWifiNetwork.pass)}
          onClick={onAddWifiNetwork}>
          {translate('admin-devices:view.sciSettings.add')}
        </PrimaryButton>
      </div>
      <div className={isBulkAction ? classes.bulkGridStyle : classes.gridStyle}>
        <IonGrid omitHeader
          rowModelType={RowModelTypes.ClientSide}
          rowData={uniqueWifiNetworks}
          totalRows={uniqueWifiNetworks?.length}
          columnDefinitions={getColumnDefinitions()}
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          checkboxesEnabled={false}
          showHeaderCheckbox={false}
          viewSubGroups={true}
          rowMenusEnabled={false}
          rowHeight={40}
          paginationEnabled
          showCustomPaginationBar
          pageOffset
          pageSizes={PAGE_SIZES}
          columnComponents={columnComponents}/>
      </div>
    </div>
  );
};

/**
* Returns column definitions for desktop Services grid
*/
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.sciSettings.ssid'),
    field      : 'ssid',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.sciSettings.passphrase'),
    field      : 'pass',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:view.events.grid.actions'),
    field        : 'delete',
    filter       : false,
    sortable     : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    cellRenderer : 'deleteCellRenderer',
    maxWidth     : 105
  }];
};

/**
  * Returns column definitions for mobile Services grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.sciSettings.ssid'),
    field      : 'ssid',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.sciSettings.passphrase'),
    field      : 'pass',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:view.events.grid.actions'),
    field        : 'delete',
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    cellRenderer : 'deleteCellRenderer',
    maxWidth     : 105
  }];
};

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});

export default SCIWifiTab;
